import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ConfigProvider, DatePicker, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { GitCompareArrows } from 'lucide-react';
import { OMS_COLORS, SOVEREIGN_CONTROL_THEME, sectionVariants } from '../../dashboard/omsDashTheme';
import { windowLabel } from '../../dashboard/utils/scopeWindow';
import type { AdminAnalytics } from '../../../../services/dashboardService';
import { GRANULARITIES } from '../utils/adminMetrics';
import type { AdminReportPayload } from '../utils/adminExport';
import { ADMIN_PRESETS, type AdminScope, type AdminScopeActions } from '../hooks/useAdminScope';
import AdminExportMenu from './AdminExportMenu';

const { RangePicker } = DatePicker;

interface Props {
  scope: AdminScope;
  actions: AdminScopeActions;
  payload: AdminAnalytics | null;
  refreshing: boolean;
  getReport: () => AdminReportPayload | null;
}

/**
 * Global scope bar — time presets, the granularity toggle every trend
 * re-buckets to, cascading State → Location → Project → Engineer filters,
 * compare-periods mode and the report download. Deep-linkable.
 */
export default function AdminScopeBar({ scope, actions, payload, refreshing, getReport }: Props) {
  const projects = payload?.projects ?? [];

  const stateOptions = useMemo(() => {
    const byState = new Map<string, number>();
    for (const p of projects) {
      const key = p.state ?? 'Unmapped';
      byState.set(key, (byState.get(key) ?? 0) + 1);
    }
    return [...byState.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([state, count]) => ({ value: state, label: `${state} (${count})` }));
  }, [projects]);

  const locationOptions = useMemo(() => {
    const byLocation = new Map<string, { name: string; count: number }>();
    for (const p of projects) {
      if (scope.state && (p.state ?? 'Unmapped') !== scope.state) continue;
      const key = p.locationId ?? 'unmapped';
      const entry = byLocation.get(key) ?? { name: p.locationName ?? 'Unmapped location', count: 0 };
      entry.count += 1;
      byLocation.set(key, entry);
    }
    return [...byLocation.entries()].map(([id, entry]) => ({
      value: id,
      label: `${entry.name} (${entry.count})`,
    }));
  }, [projects, scope.state]);

  const projectOptions = useMemo(
    () =>
      projects
        .filter(
          (p) =>
            (!scope.state || (p.state ?? 'Unmapped') === scope.state) &&
            (!scope.locationId || (p.locationId ?? 'unmapped') === scope.locationId)
        )
        .map((p) => ({ value: p.projectId, label: p.name })),
    [projects, scope.state, scope.locationId]
  );

  const engineerOptions = useMemo(
    () => (payload?.engineers ?? []).map((e) => ({ value: e.engineerId, label: e.name })),
    [payload]
  );

  const customValue: [Dayjs, Dayjs] | null =
    scope.preset === 'custom' ? [dayjs(scope.window.from), dayjs(scope.window.to)] : null;

  const projectOf = (id: string) => projects.find((p) => p.projectId === id);

  return (
    <motion.section
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
      custom={1}
      aria-label="Global scope"
      className="sticky top-16 z-30 -mx-1 px-1 lg:top-[80px]"
    >
      <div
        className="flex flex-wrap items-center gap-2 rounded-2xl border border-[rgba(11,59,46,0.28)] px-3 py-2 shadow-[0_10px_30px_-18px_rgba(42,31,23,0.35)]"
        style={{ backgroundColor: 'rgba(250,247,238,0.97)' }}
      >
        {/* Time presets */}
        <div
          className="flex items-center gap-0.5 rounded-xl border border-[rgba(11,59,46,0.24)] p-0.5"
          style={{ backgroundColor: '#fcfaf3' }}
          role="group"
          aria-label="Time window"
        >
          {ADMIN_PRESETS.filter((p) => p.key !== 'custom').map((p) => {
            const active = scope.preset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                aria-pressed={active}
                onClick={() => actions.setPreset(p.key as Exclude<typeof p.key, 'custom'>)}
                className={`rounded-lg px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition ${
                  active ? 'bg-[#14606B] text-white shadow-sm' : 'text-[#5A6275] hover:text-[#14606B]'
                }`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <ConfigProvider theme={SOVEREIGN_CONTROL_THEME}>
          <RangePicker
            size="small"
            value={customValue}
            placeholder={['Custom from', 'to']}
            format="DD MMM YYYY"
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            onChange={(dates) => {
              if (dates?.[0] && dates[1]) {
                actions.setCustomWindow({
                  from: dates[0].format('YYYY-MM-DD'),
                  to: dates[1].format('YYYY-MM-DD'),
                });
              } else {
                actions.setPreset('month');
              }
            }}
          />
        </ConfigProvider>

        {/* Granularity — the "frequency-wise" control */}
        <div
          className="flex items-center gap-0.5 rounded-xl border border-[rgba(11,59,46,0.24)] p-0.5"
          style={{ backgroundColor: '#fcfaf3' }}
          role="group"
          aria-label="Trend granularity"
        >
          {GRANULARITIES.map((g) => {
            const active = scope.granularity === g.key;
            return (
              <button
                key={g.key}
                type="button"
                aria-pressed={active}
                onClick={() => actions.setGranularity(g.key)}
                className={`rounded-lg px-2 py-1.5 text-[10px] font-bold uppercase tracking-wide transition ${
                  active ? 'bg-[#0B3B2E] text-white shadow-sm' : 'text-[#5A6275] hover:text-[#0B3B2E]'
                }`}
              >
                {g.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          aria-pressed={scope.compare}
          onClick={actions.toggleCompare}
          title="Overlay the previous equivalent window as dashed comparison lines"
          className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition ${
            scope.compare
              ? 'border-[#14606B] bg-[rgba(20,96,107,0.12)] text-[#14606B]'
              : 'border-[rgba(11,59,46,0.24)] bg-[#fcfaf3] text-[#5A6275] hover:text-[#14606B]'
          }`}
        >
          <GitCompareArrows className="h-3 w-3" /> Compare periods
        </button>

        <ConfigProvider theme={SOVEREIGN_CONTROL_THEME}>
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All states"
            aria-label="Filter by state"
            value={scope.state ?? undefined}
            options={stateOptions}
            optionFilterProp="label"
            style={{ minWidth: 130 }}
            onChange={(value) =>
              value
                ? actions.setSelection({ state: value, locationId: null, projectId: null, engineerId: null })
                : actions.drillToLevel(1)
            }
          />
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All sites"
            aria-label="Filter by site location"
            value={scope.locationId ?? undefined}
            options={locationOptions}
            optionFilterProp="label"
            style={{ minWidth: 130 }}
            onChange={(value) => {
              if (!value) {
                actions.drillToLevel(2);
                return;
              }
              const sample = projects.find((p) => (p.locationId ?? 'unmapped') === value);
              actions.setSelection({
                state: sample?.state ?? scope.state,
                locationId: value,
                projectId: null,
                engineerId: null,
              });
            }}
          />
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All projects"
            aria-label="Filter by project"
            value={scope.projectId ?? undefined}
            options={projectOptions}
            optionFilterProp="label"
            style={{ minWidth: 150 }}
            onChange={(value) => {
              if (!value) {
                actions.drillToLevel(3);
                return;
              }
              const project = projectOf(value);
              actions.setSelection({
                state: project?.state ?? scope.state,
                locationId: project ? (project.locationId ?? 'unmapped') : scope.locationId,
                projectId: value,
                engineerId: null,
              });
            }}
          />
          <Select
            size="small"
            allowClear
            showSearch
            placeholder="All engineers"
            aria-label="Filter by engineer"
            value={scope.engineerId ?? undefined}
            options={engineerOptions}
            optionFilterProp="label"
            style={{ minWidth: 140 }}
            onChange={(value) =>
              value ? actions.drillToEngineer(value) : actions.drillToLevel(scope.projectId ? 4 : scope.locationId ? 3 : scope.state ? 2 : 1)
            }
          />
        </ConfigProvider>

        <div className="ml-auto flex items-center gap-3">
          <p className="hidden items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] sm:flex" style={{ color: '#14606B' }}>
            {windowLabel(scope.window)}
            {refreshing && (
              <span
                className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ backgroundColor: OMS_COLORS.amber }}
                aria-label="Refreshing"
              />
            )}
          </p>
          <AdminExportMenu getPayload={getReport} />
        </div>
      </div>
    </motion.section>
  );
}
